import { IconButton, Image, keyframes, Spinner } from '@grupoboticario/flora-react'
import { MouseEventHandler, useMemo } from 'react'
import { IPinProduct } from 'src/@interfaces'
import { defaultPinBagIconUrl } from 'src/helpers'
import { getPinSize } from 'src/helpers/ProductPin/getPinSize'
import { useConfigCat } from 'src/hooks'

const PIN_COLOR = '#0DAB01'
const PIN_SHADOW_COLOR = `${PIN_COLOR}80`

const DEFAULT_PIN_SIZE = 28

const pulse = keyframes({
  '0%': {
    boxShadow: `0 0 0 0 ${PIN_SHADOW_COLOR}`,
  },
  '50%': {
    boxShadow: `0 0 0 5px ${PIN_SHADOW_COLOR}`,
  },
  '100%': {
    boxShadow: `0 0 0 0 ${PIN_SHADOW_COLOR}`,
  },
})

const DEFAULT_PIN_ICON = (
  <Image
    src={defaultPinBagIconUrl}
    alt=""
    size="62.5%"
    css={{ pointerEvents: 'none', touchAction: 'none' }}
  />
)

export interface PinProductProps {
  pin: IPinProduct
  pinSize?: number
  loading?: boolean
  icon?: JSX.Element
  onClick?: MouseEventHandler<HTMLButtonElement>
}

export const PinProduct = ({
  loading = false,
  pin,
  pinSize = DEFAULT_PIN_SIZE,
  icon = DEFAULT_PIN_ICON,
  ...props
}: PinProductProps) => {
  const { featureFlags } = useConfigCat()

  const pinLabel = useMemo(() => {
    const pinsLabels = pin.metadata.items.map((item) => '#' + item.sku).join(', ')
    if (featureFlags.pins_navigation) {
      return pin.metadata.items.length > 1
        ? `Visualizar produtos ${pinsLabels}`
        : `Visualizar produto ${pinsLabels}`
    }
    return pin.metadata.items.length > 1
      ? `Adicionar produtos ${pinsLabels} à sacola`
      : `Adicionar produto ${pinsLabels} à sacola`
  }, [featureFlags.pins_navigation, pin.metadata.items])

  return (
    <IconButton
      {...props}
      disabled={loading}
      className="magazine-pin-button"
      variant="unstyled"
      css={{
        animation: loading ? undefined : `${pulse} 3s infinite`,
        background: `${PIN_COLOR} !important`, // `!important` is needed to override the disabled background color when `loading=true`
        alignItems: 'center',
        borderRadius: '$pill',
        boxShadow: `0 0 0 0 ${PIN_SHADOW_COLOR}`,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        left: `${pin.x}%`,
        top: `${pin.y}%`,
        height: `${getPinSize(pinSize, pin.metadata?.height)} !important`,
        width: `${getPinSize(pinSize, pin.metadata?.width)} !important`,
      }}
      type="button"
      title={pinLabel}
      aria-label={pinLabel}
      icon={loading ? <Spinner color="$nonInteractiveAltPredominant" /> : icon}
      data-flora-text={pinLabel}
      auto-tagging="on"
    />
  )
}
